import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Settings } from 'lucide-react';
import DoctorShell from '../components/DoctorShell';
import NivaLogo from '../../components/NivaLogo';
import { useLanguage } from '../../contexts/LanguageContext';
import { useDoctorSession } from '../contexts/DoctorSessionContext';
import { doctorPatientPath, ROUTES } from '../../constants/routes';
import { doctorListPatients } from '../services/doctorApi';

function initials(name = '') {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase())
    .join('');
}

function formatDate(iso, language) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString(language === 'ar' ? 'ar-JO' : 'en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return '—';
  }
}

function greeting(language, name) {
  const hour = new Date().getHours();
  const first = (name || '').replace(/^Dr\.?\s*/i, '').split(' ')[0] || '';
  if (language === 'ar') {
    if (hour < 12) return `صباح الخير دكتور ${first}`;
    return `مساء الخير دكتور ${first}`;
  }
  const en = hour < 12 ? 'Good morning' : hour < 18 ? 'Good afternoon' : 'Good evening';
  return `${en}, Dr. ${first}`;
}

export default function DoctorPatientsScreen() {
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const { doctor } = useDoctorSession();
  const [patients, setPatients] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const data = await doctorListPatients(search.trim());
        if (!cancelled) setPatients(data.patients || []);
      } catch {
        if (!cancelled) setPatients([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 200);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [search]);

  const list = useMemo(() => patients, [patients]);

  return (
    <DoctorShell
      showMic
      showFab
      onFabClick={() => navigate(ROUTES.DOCTOR_PATIENT_NEW)}
    >
      <header className="sticky top-0 z-30 border-b border-gray-200 bg-surface px-4 py-3">
        <div className="flex items-center justify-between gap-3">
          <NivaLogo variant="header" />
          <button
            type="button"
            data-tour="doctor-nav-settings"
            onClick={() => navigate(ROUTES.DOCTOR_SETTINGS)}
            className="flex h-10 w-10 items-center justify-center rounded-full text-gray-600 hover:bg-gray-100"
            aria-label={t('doctorSettings')}
          >
            <Settings className="h-5 w-5" strokeWidth={2} />
          </button>
        </div>
        <h1 className="mt-3 text-xl font-bold text-slate-900">
          {greeting(language, doctor?.full_name || '')}
        </h1>
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t('doctorSearchPatients')}
          className="mt-3 w-full rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
        />
      </header>

      <div className="space-y-3 px-4 py-4">
        {loading && <p className="text-center text-sm text-gray-400">…</p>}
        {!loading && list.length === 0 && (
          <p className="rounded-2xl bg-white px-4 py-8 text-center text-sm text-gray-500 ring-1 ring-gray-100">
            {t('doctorNoPatientsYet')}
          </p>
        )}
        {list.map((p) => (
          <button
            key={p.id}
            type="button"
            onClick={() => navigate(doctorPatientPath(p.id))}
            className="flex w-full items-center gap-4 rounded-2xl bg-white px-4 py-5 text-left shadow-sm ring-1 ring-gray-100"
          >
            <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-primary-light text-base font-bold text-primary">
              {initials(p.full_name)}
            </div>
            <div className="min-w-0">
              <p className="truncate text-lg font-semibold text-slate-900">{p.full_name}</p>
              <p className="mt-0.5 text-sm text-gray-500">
                {[p.age, p.gender].filter(Boolean).join(' · ') || '—'}
                {' · '}
                {formatDate(p.last_visit_at, language)}
              </p>
            </div>
          </button>
        ))}
      </div>
    </DoctorShell>
  );
}
